import React from 'react'
import { createPortal } from 'react-dom'
import { Icon, Button } from '@/design-system/primitives'

// ─── SidePeek ────────────────────────────────────────────────────────
export interface SidePeekProps {
  open: boolean
  onClose?: () => void
  width?: number
  children: React.ReactNode
  style?: React.CSSProperties
  /**
   * 'fixed' (default — viewport side panel)
   * 'absolute' (inside position:relative parent)
   * 'fullscreen' (mobile fullscreen overlay, slides up from bottom)
   */
  anchor?: 'fixed' | 'absolute' | 'fullscreen'
  /**
   * Whether clicking outside the panel closes it. Default true (existing
   * behavior, unchanged for current consumers). Set false for panels that
   * hold in-progress edits — an accidental outside click should never
   * silently discard unsaved work; Esc and the explicit close button remain
   * available either way.
   */
  closeOnOutsideClick?: boolean
  /**
   * Panel z-index for the 'fixed' anchor (UOW-4G stacking ladder). The
   * outside-click catcher moves WITH the panel (one below it) — raising
   * only the panel via `style` would leave the catcher at 199, so with the
   * panel stacked over a SlideOver (z 901) every outside click would land
   * on the SlideOver's backdrop below and close IT instead, silently
   * discarding whatever that layer held. Default 200 — byte-identical to
   * the pre-prop behavior for every existing consumer.
   */
  zIndex?: number
}

export function SidePeek({ open, onClose, width = 480, children, style, anchor = 'fixed', closeOnOutsideClick = true, zIndex = 200 }: SidePeekProps) {
  // Esc closes the peek regardless of anchor mode — every consumer gets
  // this for free instead of each one re-implementing its own listener.
  React.useEffect(() => {
    if (!open || !onClose) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (anchor === 'fullscreen') {
    // Portaled to document.body — NOT rendered inline. Mobile pages mount
    // inside MobileLayout's `-webkit-overflow-scrolling: touch` scroll
    // container, which on iOS WebKit traps position:fixed descendants
    // (regardless of z-index): the "fullscreen" panel rendered between the
    // app header and the bottom nav, clipping its bottom action bar — the
    // daily-log Save draft / Submit log buttons simply didn't exist on the
    // native app (#3612). Desktop Blink ignores that CSS, so web looked
    // fine. Same #1418 class + fix as BottomSheet.tsx.
    return createPortal(
      <>
        {/* Backdrop */}
        <div
          onClick={closeOnOutsideClick ? onClose : undefined}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 299,
            background: 'rgba(0,0,0,0.5)',
            opacity: open ? 1 : 0,
            pointerEvents: open ? 'auto' : 'none',
            transition: 'opacity 0.2s ease-out',
          }}
          aria-hidden="true"
        />
        {/* Fullscreen panel slides up from bottom.
            paddingTop respects iOS safe area (dynamic island / notch) so the
            peek's header sits below the status bar, not under it. */}
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 300,
            background: 'var(--bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            transform: open ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform 0.25s var(--ease-out, ease-out)',
            overflowY: 'hidden',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            ...style,
          }}
        >
          {children}
        </div>
      </>,
      document.body,
    )
  }

  const positioning: React.CSSProperties = anchor === 'absolute'
    ? { position: 'absolute', top: 0, right: 0, bottom: 0, zIndex: 5 }
    : { position: 'fixed', top: 0, right: 0, bottom: 0, zIndex }

  return (
    <>
      {anchor === 'fixed' && (
        <div
          onClick={closeOnOutsideClick ? onClose : undefined}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: zIndex - 1,
            pointerEvents: open ? 'auto' : 'none',
            opacity: 0,
          }}
          aria-hidden="true"
        />
      )}
      <div
        style={{
          ...positioning,
          width,
          maxWidth: anchor === 'fixed' ? '100vw' : undefined,
          background: 'var(--bg-surface)',
          borderLeft: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: `transform 0.2s var(--ease-out, ease-out)`,
          boxShadow: open ? '-8px 0 32px rgba(0,0,0,0.18)' : 'none',
          ...style,
        }}
      >
        {children}
      </div>
    </>
  )
}

// ─── PeekHeader ──────────────────────────────────────────────────────
export interface PeekHeaderProps {
  title: string
  subtitle?: string
  icon?: string
  onClose?: () => void
  actions?: React.ReactNode
  style?: React.CSSProperties
}

export function PeekHeader({ title, subtitle, icon, onClose, actions, style }: PeekHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 12px',
        height: 52,
        flexShrink: 0,
        borderBottom: '1px solid var(--border)',
        fontFamily: 'var(--font-sans)',
        ...style,
      }}
    >
      {icon && (
        <Icon name={icon} size={16} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--text)',
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-muted)',
              lineHeight: 1.3,
              marginTop: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {actions && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {actions}
        </div>
      )}
      {onClose && (
        <Button
          variant="ghost"
          size="sm"
          icon="x"
          onClick={onClose}
          aria-label="Close"
          style={{ flexShrink: 0, marginLeft: actions ? 0 : 4 }}
        />
      )}
    </div>
  )
}

// ─── PeekSection ─────────────────────────────────────────────────────
export interface PeekSectionProps {
  title?: string
  children: React.ReactNode
  style?: React.CSSProperties
}

export function PeekSection({ title, children, style }: PeekSectionProps) {
  return (
    <div
      style={{
        borderTop: '1px solid var(--border)',
        padding: '12px 16px',
        fontFamily: 'var(--font-sans)',
        ...style,
      }}
    >
      {title && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase' as const,
            color: 'var(--text-muted)',
            marginBottom: 10,
          }}
        >
          {title}
        </div>
      )}
      {children}
    </div>
  )
}

// ─── PeekMetaRow ─────────────────────────────────────────────────────
export interface PeekMetaRowProps {
  label: string
  children: React.ReactNode
  style?: React.CSSProperties
}

export function PeekMetaRow({ label, children, style }: PeekMetaRowProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 28,
        marginBottom: 4,
        fontFamily: 'var(--font-sans)',
        ...style,
      }}
    >
      <div
        style={{
          width: 120,
          flexShrink: 0,
          fontSize: 12,
          color: 'var(--text-muted)',
          lineHeight: 1.4,
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text)' }}>
        {children}
      </div>
    </div>
  )
}
